import React from 'react'
import { cn } from 'lib/utils'
import NotesRailSection from './NotesRailSection'
import type { NoteFacets, NoteFilterId } from './notesWorkspace.types'

interface NotesRailFiltersProps {
	facets: NoteFacets
	filter: NoteFilterId
	onFilterChange: (filter: NoteFilterId) => void
	/** Hidden on portfolio notes, which cannot thread. */
	supportsReplies: boolean
	className?: string
}

interface FilterOption {
	id: NoteFilterId
	label: string
	count: number
}

/**
 * Quick filters plus the at-a-glance counts.
 *
 * Every count here is derived from the note set already in Redux. The note
 * endpoints accept only an entity id and sort is hardcoded server-side, so
 * there is nothing to push down to the API even if we wanted to.
 */
const NotesRailFilters: React.FC<NotesRailFiltersProps> = ({
	facets,
	filter,
	onFilterChange,
	supportsReplies,
	className,
}) => {
	const options: FilterOption[] = [
		{ id: 'all', label: 'All notes', count: facets.counts.notes },
		{ id: 'images', label: 'With images', count: facets.counts.notesWithImages },
		{ id: 'mine', label: 'Mine', count: -1 },
	]

	if (supportsReplies) {
		options.push({ id: 'replies', label: 'Has replies', count: -1 })
	}

	const stats = [
		{ label: 'Notes', value: facets.counts.notes },
		{ label: 'Images', value: facets.counts.images },
		{ label: 'People', value: facets.counts.contributors },
		...(supportsReplies ? [{ label: 'Replies', value: facets.counts.replies }] : []),
	]

	return (
		<div className={cn('space-y-4', className)}>
			<NotesRailSection title="At a glance">
				<dl className="grid grid-cols-2 gap-3">
					{stats.map(stat => (
						<div key={stat.label} data-testid={`notes-stat-${stat.label.toLowerCase()}`}>
							<dt className="text-xs text-muted-foreground">{stat.label}</dt>
							<dd className="m-0 text-lg font-semibold text-foreground">{stat.value}</dd>
						</div>
					))}
				</dl>
			</NotesRailSection>

			<NotesRailSection title="Filter">
				<div className="flex flex-col gap-1">
					{options.map(option => {
						const isActive = filter === option.id
						return (
							<button
								key={option.id}
								type="button"
								onClick={() => onFilterChange(option.id)}
								aria-pressed={isActive}
								data-testid={`notes-filter-${option.id}`}
								className={cn(
									'flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors',
									isActive
										? 'bg-primary/10 font-medium text-primary'
										: 'text-foreground hover:bg-muted',
								)}
							>
								<span>{option.label}</span>
								{option.count >= 0 && (
									<span
										className={cn(
											'text-xs tabular-nums',
											isActive ? 'text-primary' : 'text-muted-foreground',
										)}
									>
										{option.count}
									</span>
								)}
							</button>
						)
					})}
				</div>
			</NotesRailSection>
		</div>
	)
}

export default NotesRailFilters
